/**
 * 10명 이상 단체 구매 50% 이벤트 (/events/group-50) 캠페인 상수.
 *
 * 할인 정의: promo=group50 경로에서 인원 10명 이상이면 현재 판매가의 50% 단가.
 * (예: 6개월 110,000원 → 55,000원/인) 10명 미만은 기존 인원별 티어로 폴백.
 * 가격 규칙의 단일 소스는 utils/groupPricing.js(GROUP50_EVENT_*)이고,
 * 서버(backend/services/group_pricing.py)가 같은 규칙으로 금액을 재계산·검증한다.
 */
import {
  GROUP50_EVENT_DISCOUNT_RATE,
  GROUP50_EVENT_ENABLED,
  GROUP50_EVENT_MIN_QUANTITY,
  GROUP50_EVENT_PROMO_CODE,
  GROUP50_EVENT_SLUG,
  GROUP50_EVENT_TIER_LABEL,
  GROUP_PLAN_PRICES,
  calculateGroupPrice,
  normalizeGroupPlanId,
} from './groupPricing';
import { isGroup50Period } from './pro50Campaign';

export const GROUP50_PROMO_CODE = GROUP50_EVENT_PROMO_CODE;
export const GROUP50_PROMO_SLUG = GROUP50_EVENT_SLUG;
export const GROUP50_MIN_QUANTITY = GROUP50_EVENT_MIN_QUANTITY;
export const GROUP50_DISCOUNT_RATE = GROUP50_EVENT_DISCOUNT_RATE;
export { GROUP50_EVENT_ENABLED };
export const GROUP50_CAMPAIGN = 'group50_2026q3';
export const GROUP50_LANDING_PATH = '/events/group-50';
export const GROUP50_DEFAULT_PLAN_ID = '6month';
export const GROUP50_TIER_LABEL = GROUP50_EVENT_TIER_LABEL;

// 2026-09-30 23:59:59 KST 자동 마감 (pro50Campaign.GROUP50_EVENT_END). 10월부터는 /events/pro-50으로 대체된다.
export const GROUP50_EVENT_END_LABEL = '9월 30일까지';

export const isGroup50EventActive = (now = new Date()) => GROUP50_EVENT_ENABLED && isGroup50Period(now);

// 사이트 내부 진입점(사이드바·요금제·프로필 메뉴 등) → 랜딩 URL.
// UTM을 건드리지 않는 `from` 파라미터로만 진입점을 구분해 광고 어트리뷰션을 오염시키지 않는다.
// 랜딩의 landing_viewed 트래킹이 `from`을 entry로 읽는다.
export const GROUP50_ENTRY_PARAM = 'from';
export const buildGroup50LandingUrl = (entry = '') => {
  const token = String(entry || '').trim().slice(0, 40).replace(/[^a-zA-Z0-9._-]/g, '_');
  return token ? `${GROUP50_LANDING_PATH}?${GROUP50_ENTRY_PARAM}=${token}` : GROUP50_LANDING_PATH;
};

// 모바일 햄버거·프로필 메뉴의 "새 소식" 도트 — 랜딩을 한 번 본 사용자에게는 더 이상 보이지 않는다.
// (시크릿 모드·공용 PC에서는 다시 보일 수 있지만, 도트는 정보 힌트일 뿐 흐름을 막지 않으므로 허용)
export const GROUP50_ENTRY_SEEN_KEY = 'group50_entry_seen';
export const hasSeenGroup50Entry = () => {
  try {
    return localStorage.getItem(GROUP50_ENTRY_SEEN_KEY) === '1';
  } catch {
    return true; // 저장소를 못 쓰면 도트를 숨겨 소음을 만들지 않는다.
  }
};
export const markGroup50EntrySeen = () => {
  try {
    localStorage.setItem(GROUP50_ENTRY_SEEN_KEY, '1');
  } catch { /* 저장 실패는 무시 */ }
};

export const isGroup50PromoCode = (code) => (
  isGroup50EventActive()
  && String(code || '').trim().toUpperCase() === GROUP50_PROMO_CODE
);

export const getGroup50UnitPrice = (planId) => {
  const plan = GROUP_PLAN_PRICES[normalizeGroupPlanId(planId)];
  return Math.round(plan.price * (1 - GROUP50_DISCOUNT_RATE));
};

/**
 * calculateGroupPrice에 위임한다 — 10명 미만(또는 이벤트 종료)이면
 * 기존 인원별 티어로 폴백하는 규칙까지 groupPricing이 백엔드와 동일하게 처리한다.
 */
export const calculateGroup50Price = (planId, quantity = 1) => (
  calculateGroupPrice(planId, quantity, GROUP50_PROMO_CODE)
);

/** 랜딩 가격 비교표용 — 플랜별 정가/이벤트가(1인) */
export const getGroup50PricingRows = () => (
  Object.entries(GROUP_PLAN_PRICES).map(([planId, plan]) => ({
    planId,
    label: plan.label,
    basePrice: plan.price,
    eventPrice: Math.round(plan.price * (1 - GROUP50_DISCOUNT_RATE)),
  }))
);

export const buildGroup50PurchaseUrl = (
  planId = GROUP50_DEFAULT_PLAN_ID,
  quantity = GROUP50_MIN_QUANTITY,
) => `/bulk-purchase?promo=${GROUP50_PROMO_SLUG}&plan=${normalizeGroupPlanId(planId)}&qty=${Math.max(1, Number(quantity) || GROUP50_MIN_QUANTITY)}`;

// 견적서(학교 예산·행정서류) 흐름 — /bulk-purchase 안의 견적 섹션으로 promo를 보존한 채 이동한다.
// 백엔드가 promo를 견적 PDF·결제 링크에도 보존한다.
export const buildGroup50QuotationUrl = (
  planId = GROUP50_DEFAULT_PLAN_ID,
  quantity = GROUP50_MIN_QUANTITY,
) => `${buildGroup50PurchaseUrl(planId, quantity)}&intent=quote`;
